/**
 * Link prioritization sessions to a Catchball loop (Catchball) and preserve
 * desk context (`loopId` / `ballId`) across session → live → results routes.
 */

import { normalizeLoopState } from '../types/prioritization/catchballLoop';
import { useCatchballLoopStore } from '../stores/prioritization/catchballLoopStore';

export type CatchballNavQuery = {
  loopId?: string | null;
  ballId?: string | null;
  trackId?: string | null;
  cycleId?: string | null;
};

/**
 * Event Designer at `/prioritization/sessions/new` (S1–S5). Prefer this over
 * `/explore` (flag-gated) and over Session Detail (`/sessions/:id`), which is
 * the retired setup console.
 */
export function eventDesignerHref(opts: CatchballNavQuery & { resume?: string | null } = {}): string {
  const resume = opts.resume?.trim();
  const path = resume
    ? `/prioritization/sessions/new?resume=${encodeURIComponent(resume)}`
    : '/prioritization/sessions/new';
  return withCatchballQuery(path, opts);
}

export function hasV3Config(session: { modeConfig?: unknown }): boolean {
  const mc = session.modeConfig as { v3Config?: unknown } | undefined;
  return mc?.v3Config != null && typeof mc.v3Config === 'object';
}

export function isV3SessionRunning(session: { modeConfig?: unknown }): boolean {
  const steps = (session.modeConfig as { v3Config?: { steps?: Record<string, { runStatus?: string }> } } | undefined)
    ?.v3Config?.steps;
  if (!steps) return false;
  return Object.values(steps).some((s) => s.runStatus === 'running');
}

/**
 * Where a sessions-list card should open: live room if a V3 step is running,
 * Event Designer for V3 setup, participant brief for a joinable classic session.
 */
export function sessionSurfaceOpenHref(session: {
  id: string;
  modeConfig?: unknown;
  trackId?: string | null;
  cycleId?: string | null;
}): string {
  if (isV3SessionRunning(session)) {
    return `/prioritization/sessions/${session.id}/live`;
  }
  if (hasV3Config(session)) {
    return eventDesignerHref({
      resume: session.id,
      trackId: session.trackId,
      cycleId: session.cycleId,
    });
  }
  return `/prioritization/session/${session.id}`;
}

/** Merge catchball context into a path, keeping any existing query string. */
export function withCatchballQuery(path: string, opts: CatchballNavQuery): string {
  const qIndex = path.indexOf('?');
  const base = qIndex >= 0 ? path.slice(0, qIndex) : path;
  const params = new URLSearchParams(qIndex >= 0 ? path.slice(qIndex + 1) : '');
  if (opts.loopId) params.set('loopId', opts.loopId);
  if (opts.ballId) params.set('ballId', opts.ballId);
  if (opts.trackId) params.set('trackId', opts.trackId);
  if (opts.cycleId) params.set('cycleId', opts.cycleId);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/** Read desk context from URLSearchParams (or a plain record). */
export function catchballQueryFromSearch(
  search: URLSearchParams | { get: (key: string) => string | null },
): CatchballNavQuery {
  return {
    loopId: search.get('loopId'),
    ballId: search.get('ballId'),
    trackId: search.get('trackId'),
    cycleId: search.get('cycleId'),
  };
}

/**
 * Resolve which loop a session belongs to for Catchball navigation.
 *
 * Order:
 * 1. URL `loopId`
 * 2. Loop that already lists this sessionId
 * 3. Open loop on the same track (+ cycle when known)
 * 4. Sole open loop on the cycle (covers sessions that lost track_id)
 */
export function resolveLoopIdForSession(opts: {
  urlLoopId?: string | null;
  sessionId?: string | null;
  trackId?: string | null;
  cycleId?: string | null;
  /** Optional snapshot — pass from a React selector so memos recompute correctly. */
  loops?: ReturnType<typeof useCatchballLoopStore.getState>['loops'];
}): string | null {
  if (opts.urlLoopId) return opts.urlLoopId;
  const loops = opts.loops ?? useCatchballLoopStore.getState().loops;

  if (opts.sessionId) {
    const bySession = loops.find((l) => l.sessionIds?.includes(opts.sessionId!));
    if (bySession) return bySession.id;
  }

  if (opts.trackId) {
    const onTrack = loops.filter(
      (l) =>
        l.trackId === opts.trackId &&
        (!opts.cycleId || l.cycleId === opts.cycleId),
    );
    if (onTrack.length > 0) {
      const open = onTrack.find((l) => normalizeLoopState(l.state) === 'OPEN');
      return (open ?? onTrack[0]).id;
    }
  }

  // V3 / live runs sometimes persist cycle_id without track_id. Fall back to the
  // cycle's open loop so results can still surface Attach → Catchball.
  if (opts.cycleId) {
    const onCycle = loops.filter((l) => l.cycleId === opts.cycleId);
    const open = onCycle.filter((l) => normalizeLoopState(l.state) === 'OPEN');
    if (open.length === 1) return open[0].id;
    if (open.length > 1) {
      // Prefer a loop that already has sittings, else the first open loop.
      const withSessions = open.find((l) => (l.sessionIds?.length ?? 0) > 0);
      return (withSessions ?? open[0]).id;
    }
    if (onCycle.length === 1) return onCycle[0].id;
  }

  return null;
}

/**
 * Why a sitting did not end up on its ball. A completed session that silently
 * fails to attach is invisible to the desk that ran it (prod 2026-07-27: loop
 * 4604c49c had session_ids [] with a RUN_COMPLETE session on its own track), so
 * every failure mode is named rather than collapsed into `false`.
 */
export type SessionLinkFailure =
  /** No loop could be resolved from the URL, the track, or the cycle. */
  | 'no_loop'
  /** A loop was found but the write was refused or errored. */
  | 'link_failed';

export interface SessionLinkOutcome {
  ok: boolean;
  loopId: string | null;
  failure?: SessionLinkFailure;
  /** Server-supplied reason, when there is one. */
  message?: string;
}

/** Append sessionId onto the loop's sessionIds (no-op if already linked). */
export async function linkSessionToCatchballLoop(
  loopId: string | null | undefined,
  sessionId: string | null | undefined,
): Promise<boolean> {
  if (!loopId || !sessionId) return false;
  try {
    const ok = await useCatchballLoopStore.getState().addSessionToLoop(loopId, sessionId);
    return ok !== false;
  } catch (err) {
    console.error('[catchball] linkSessionToCatchballLoop failed:', err);
    return false;
  }
}

/**
 * Resolve the loop for a session and attach it, reporting what happened.
 *
 * Use this on session close instead of resolve-then-link: the old call sites
 * passed only `urlLoopId` and `sessionId`, so `resolveLoopIdForSession` could
 * never reach its track or cycle fallbacks — a session closed without `loopId`
 * in the URL resolved to null and attached to nothing, with the miss swallowed.
 */
export async function attachSessionToResolvedLoop(opts: {
  sessionId: string;
  urlLoopId?: string | null;
  trackId?: string | null;
  cycleId?: string | null;
}): Promise<SessionLinkOutcome> {
  const loopId = resolveLoopIdForSession({
    urlLoopId: opts.urlLoopId,
    sessionId: opts.sessionId,
    trackId: opts.trackId,
    cycleId: opts.cycleId,
  });
  if (!loopId) {
    console.warn(
      '[catchball] no loop resolved for session on close — it will show as "Not attached" in the Inbox',
      { sessionId: opts.sessionId, trackId: opts.trackId, cycleId: opts.cycleId },
    );
    return { ok: false, loopId: null, failure: 'no_loop' };
  }

  const ok = await linkSessionToCatchballLoop(loopId, opts.sessionId);
  if (ok) return { ok: true, loopId };

  const message = useCatchballLoopStore.getState().error ?? undefined;
  console.warn('[catchball] session close could not attach to loop', {
    sessionId: opts.sessionId,
    loopId,
    message,
  });
  return { ok: false, loopId, failure: 'link_failed', message };
}
